import { memo, useState, useEffect } from "react";
import styled from "styled-components";
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Spin, Image, Button, Tooltip, message, Empty } from "antd";
import {
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  DownOutlined,
  UpOutlined,
  CopyOutlined,
  EnvironmentOutlined,
  DatabaseOutlined,
  ProfileOutlined,
  PictureOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  SyncOutlined,
  CloudUploadOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { CollectItemFileAPIs } from '~/common/typescript/apis/CollectItemFileAPIs';
import { CollectItemAPIs, CollectItem_GetOne_Response } from '~/common/typescript/apis/CollectItemAPIs';
import { CollectFormAPIs, CollectForm_GetOne_Response } from '~/common/typescript/apis/CollectFormAPIs';
import { API_URL_ADMIN } from '~/config/config';
import { collectItemPopupPropsAtom, triggerRefreshCollectItems } from "../../controllers/popups/CollectItemPopup";
import {
  allCollectFormsMapAtom,
  singleFormsCacheAtom,
} from "../../panels/GeocollectDatatablePanel/atoms/CollectFormAtoms";
import CollectFormDetailDrawer from "../../controllers/popups/CollectFormDetailDrawer";
import MapUtils from "../../utils/MapUtils";
import GeocollectItemProperties from "./GeocollectItemProperties";
import { formatDateTime } from "~/utils/dateUtils";
import DateTimeViewer from "~/components/common/DateTimeViewer";

const GeocollectItemPanel = () => {
  const [props, setProps] = useAtom(collectItemPopupPropsAtom);
  const triggerRefresh = useSetAtom(triggerRefreshCollectItems);
  const allFormsMap = useAtomValue(allCollectFormsMapAtom);
  const setSingleFormsCache = useSetAtom(singleFormsCacheAtom);

  const [collectItemData, setCollectItemData] = useState<CollectItem_GetOne_Response | null>(null);
  const [collectFormData, setCollectFormData] = useState<CollectForm_GetOne_Response | null>(null);
  const [collectFormName, setCollectFormName] = useState<string>('');
  const [loadingForm, setLoadingForm] = useState(false);
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false);

  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingItemData, setLoadingItemData] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentPreviewImage, setCurrentPreviewImage] = useState<any>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);

  const isVisible = !!props;
  const items = props?.items && props.items.length > 0 ? props.items : null;
  const currentIndex = props?.selectedIndex ?? 0;
  const totalItems = items ? items.length : 1;

  const currentFormUuid =
    collectItemData?.collect_form_uuid || props?.properties?.collect_form_uuid;
  const currentFormFromMap = currentFormUuid ? allFormsMap[currentFormUuid] : null;
  const activeFormData = collectFormData || currentFormFromMap;
  const activeFormName = collectFormName || currentFormFromMap?.name || '';

  const handleSelectCandidate = (nextIndex: number) => {
    if (!props || !items || items.length <= 1) return;
    const normalizedIndex = ((nextIndex % items.length) + items.length) % items.length;
    const candidate = items[normalizedIndex];
    if (!candidate) return;
    setProps({
      ...props,
      selectedIndex: normalizedIndex,
      title: candidate.title,
      properties: candidate.properties,
      geometry: candidate.geometry,
    });
    if (candidate.geometry) {
      MapUtils.highlightFeatureCollection({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: candidate.properties,
            geometry: candidate.geometry,
          },
        ],
      } as any);
    }
  };

  // Fetch full collect item data when panel opens
  useEffect(() => {
    if (props?.properties?.uuid) {
      setLoadingItemData(true);
      setCollectItemData(null);

      CollectItemAPIs.remote_getOne(props.properties.uuid)
        .then((data) => {
          setCollectItemData(data);

          // Highlight full geometry from server
          const rawGeom = data?.geom || (data as any)?.geom_data || (data as any)?.geometry;
          if (rawGeom) {
            try {
              const parsedGeom = typeof rawGeom === 'string' ? JSON.parse(rawGeom) : rawGeom;
              if (parsedGeom && typeof parsedGeom === 'object') {
                MapUtils.highlightFeatureCollection(parsedGeom);
              }
            } catch {
              // Ignore non-JSON geometry strings
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching collect item data:', error);
          message.error('Không thể tải thông tin chi tiết');
        })
        .finally(() => {
          setLoadingItemData(false);
        });
    }
  }, [props?.properties?.uuid]);

  // Fetch collect form data when currentFormUuid is available
  useEffect(() => {
    if (!currentFormUuid) {
      setCollectFormData(null);
      setCollectFormName('');
      return;
    }

    if (allFormsMap[currentFormUuid]) {
      setCollectFormData(allFormsMap[currentFormUuid]);
      setCollectFormName(allFormsMap[currentFormUuid].name || '');
      return;
    }

    setLoadingForm(true);
    CollectFormAPIs.remote_getOne(currentFormUuid)
      .then((formData) => {
        setCollectFormData(formData);
        setCollectFormName(formData.name || '');
        setSingleFormsCache((prev) => ({ ...prev, [currentFormUuid]: formData }));
      })
      .catch((error) => {
        console.error('Error fetching collect form in panel:', error);
      })
      .finally(() => {
        setLoadingForm(false);
      });
  }, [currentFormUuid, allFormsMap, setSingleFormsCache]);

  // Fetch images when panel opens
  useEffect(() => {
    const itemUuid = props?.properties?.uuid;
    const formUuid = currentFormUuid;

    if (itemUuid && formUuid) {
      setLoading(true);
      setImages([]);

      // Step 1: Get list of UUIDs
      CollectItemFileAPIs.remote_getPageSyncable({
        collect_item_uuid: itemUuid,
        page: 1,
        pageSize: 20,
      })
        .then((response) => {
          if (response.results && response.results.length > 0) {
            // Step 2: Extract UUIDs and fetch detailed information
            const uuids = response.results.map((item: any) => item.uuid);

            return CollectItemFileAPIs.remote_getListWithUUIDs({
              collect_form_uuid: formUuid,
              uuids: uuids,
            });
          }
          return [];
        })
        .then((detailedFiles) => {
          if (detailedFiles && detailedFiles.length > 0) {
            // Map the detailed results to image objects
            const imageFiles = detailedFiles.map((file: any) => ({
              uuid: file.uuid,
              url: `${API_URL_ADMIN}/api/geocollect/restore/download-file/${file.uuid}`,
              name: file.name || file.uuid.split('__')[1] || file.uuid,
              user: file.user,
              createdAt: file.created_at,
              updatedAt: file.updated_at,
              metadata: file.metadata,
            }));

            setImages(imageFiles);
          }
        })
        .catch((error) => {
          console.error('Error fetching collect item files:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [props?.properties?.uuid, currentFormUuid]);

  const handleClose = () => {
    setProps(null);
    setCollectItemData(null);
    setCollectFormData(null);
    setCollectFormName('');
    setImages([]);
    setIsMaximized(false);
    setIsMetadataExpanded(false);
    setIsFormDrawerOpen(false);
    MapUtils.triggerResizeEvent();
  };

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
    MapUtils.triggerResizeEvent();
  };

  // Generate dynamic title and subtitle
  const generateTitle = () => {
    if (!props?.properties?.uuid) return 'Dữ liệu thu thập';

    const uuid = props.properties.uuid;
    const last8Digits = uuid.slice(-8);
    const name = props.properties.name || props.properties.title || '';

    return `#${last8Digits}${name ? `, ${name}` : ''}`;
  };

  const generateSubtitle = () => {
    const description = props.properties?.description || props.properties?.desc || 'Chi tiết thông tin điểm thu thập';
    return description.length > 30 ? description.substring(0, 30) + '...' : description;
  };

  const toggleMetadata = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMetadataExpanded(!isMetadataExpanded);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success('Đã copy tọa độ vào clipboard');
      })
      .catch(() => {
        message.error('Không thể copy tọa độ');
      });
  };

  if (!isVisible) return null;

  return (
    <UI_Wrapper className={isMaximized ? 'maximized' : ''}>
      <UI_Header>
        <UI_HeaderLeft>
          <UI_HeaderIcon>
            <EnvironmentOutlined style={{ fontSize: 16, color: '#ffffff' }} />
          </UI_HeaderIcon>
          <UI_HeaderTitle>
            {activeFormName && (
              <UI_FormBadge
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFormDrawerOpen(true);
                }}
                title="Nhấn để xem thông tin biểu mẫu"
              >
                <FileTextOutlined style={{ marginRight: 4, fontSize: 11 }} />
                <span>Biểu mẫu: {activeFormName}</span>
              </UI_FormBadge>
            )}
            <UI_HeaderMainTitle>{generateTitle()}</UI_HeaderMainTitle>
            <UI_HeaderSubtitle>{generateSubtitle()}</UI_HeaderSubtitle>
          </UI_HeaderTitle>
        </UI_HeaderLeft>
        <UI_HeaderActions>
          {totalItems > 1 && (
            <UI_Pager data-testid="collect-panel-pager">
              <UI_PagerButton
                type="button"
                data-testid="collect-panel-prev"
                title="Đối tượng trước"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectCandidate(currentIndex - 1);
                }}
              >
                <LeftOutlined style={{ fontSize: 10 }} />
              </UI_PagerButton>
              <UI_PagerText data-testid="collect-panel-page-indicator">
                {currentIndex + 1}/{totalItems}
              </UI_PagerText>
              <UI_PagerButton
                type="button"
                data-testid="collect-panel-next"
                title="Đối tượng tiếp theo"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectCandidate(currentIndex + 1);
                }}
              >
                <RightOutlined style={{ fontSize: 10 }} />
              </UI_PagerButton>
            </UI_Pager>
          )}
          <Tooltip title={isMaximized ? "Thu nhỏ" : "Phóng to"}>
            <Button
              type="text"
              size="small"
              icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={handleMaximize}
            />
          </Tooltip>
          <Tooltip title="Đóng">
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleClose}
            />
          </Tooltip>
        </UI_HeaderActions>
      </UI_Header>

      <UI_Content>
        {/* Metadata Section - Collapsible */}
        <UI_MetadataSection>
          <UI_MetadataHeader onClick={toggleMetadata}>
            <UI_MetadataTitle>
              <DatabaseOutlined style={{ color: '#3b82f6' }} />
              <span>Siêu dữ liệu</span>
            </UI_MetadataTitle>
            <UI_ExpandButton>
              {isMetadataExpanded ? <UpOutlined /> : <DownOutlined />}
            </UI_ExpandButton>
          </UI_MetadataHeader>

          {isMetadataExpanded && (
            <UI_MetadataContent>
              {loadingItemData ? (
                <UI_LoadingWrapper>
                  <Spin size="small" />
                  <span style={{ marginLeft: 8 }}>Đang tải thông tin...</span>
                </UI_LoadingWrapper>
              ) : (
                <>
                  {/* Coordinates with Copy */}
                  {props?.lngLat?.lng !== undefined && props?.lngLat?.lat !== undefined && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <EnvironmentOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Tọa độ:</span>
                      </UI_MetadataLabel>
                      <UI_CoordinateContainer>
                        <UI_CoordinateText>
                          {props.lngLat.lat.toFixed(6)}, {props.lngLat.lng.toFixed(6)}
                        </UI_CoordinateText>
                        <Tooltip title="Copy tọa độ">
                          <UI_CopyButton
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(`${props.lngLat.lat.toFixed(6)}, ${props.lngLat.lng.toFixed(6)}`);
                            }}
                          />
                        </Tooltip>
                      </UI_CoordinateContainer>
                    </UI_MetadataRow>
                  )}

                  {/* Form Name */}
                  {activeFormName && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <FileTextOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Biểu mẫu:</span>
                      </UI_MetadataLabel>
                      <UI_MetadataValue>
                        <UI_FormLink
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsFormDrawerOpen(true);
                          }}
                          title="Nhấn để xem chi tiết biểu mẫu"
                        >
                          {activeFormName}
                        </UI_FormLink>
                      </UI_MetadataValue>
                    </UI_MetadataRow>
                  )}

                  {/* Creator */}
                  {collectItemData?.user && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <UserOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Người tạo:</span>
                      </UI_MetadataLabel>
                      <UI_MetadataValue>
                        {collectItemData.user.fullname || collectItemData.user.username || 'Không rõ'}
                      </UI_MetadataValue>
                    </UI_MetadataRow>
                  )}

                  {/* Created Date */}
                  {collectItemData?.client_created_at && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <CalendarOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Ngày tạo:</span>
                      </UI_MetadataLabel>
                      <UI_MetadataValue>
                        <DateTimeViewer date={collectItemData.client_created_at} initialMode="relative" />
                      </UI_MetadataValue>
                    </UI_MetadataRow>
                  )}

                  {/* Sync Date */}
                  {collectItemData?.sync_at && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <SyncOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Ngày đồng bộ:</span>
                      </UI_MetadataLabel>
                      <UI_MetadataValue>
                        <DateTimeViewer date={collectItemData.sync_at} initialMode="relative" />
                      </UI_MetadataValue>
                    </UI_MetadataRow>
                  )}

                  {/* Created At (Server) */}
                  {collectItemData?.created_at && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <CloudUploadOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Ngày tạo (server):</span>
                      </UI_MetadataLabel>
                      <UI_MetadataValue>
                        <DateTimeViewer date={collectItemData.created_at} initialMode="relative" />
                      </UI_MetadataValue>
                    </UI_MetadataRow>
                  )}

                  {/* Updated At */}
                  {collectItemData?.updated_at && (
                    <UI_MetadataRow>
                      <UI_MetadataLabel>
                        <ClockCircleOutlined style={{ marginRight: 6, color: '#64748b' }} />
                        <span>Ngày cập nhật:</span>
                      </UI_MetadataLabel>
                      <UI_MetadataValue>
                        <DateTimeViewer date={collectItemData.updated_at} initialMode="relative" />
                      </UI_MetadataValue>
                    </UI_MetadataRow>
                  )}
                </>
              )}
            </UI_MetadataContent>
          )}
        </UI_MetadataSection>

        {/* Properties Section */}
        <UI_PropertiesSection>
          <UI_SectionTitle>
            <ProfileOutlined style={{ color: '#3b82f6' }} />
            <span>Trường thông tin</span>
          </UI_SectionTitle>
          <GeocollectItemProperties
            collectItemData={collectItemData}
            collectFormUuid={currentFormUuid}
            loading={loadingItemData}
            onSave={async (properties) => {
              if (!props?.properties?.uuid) return;

              try {
                const response = await CollectItemAPIs.remote_update(props.properties.uuid, {
                  properties,
                });

                // Update local state with new data
                setCollectItemData(response.data);

                // Trigger refresh of data table
                triggerRefresh();

                message.success('Đã lưu thay đổi thành công');
              } catch (error) {
                console.error('Error updating collect item:', error);
                throw error;
              }
            }}
          />
        </UI_PropertiesSection>

        {/* Images Section */}
        <UI_ImagesSection>
          <UI_SectionTitle>
            <PictureOutlined style={{ color: '#3b82f6' }} />
            <span>Ảnh thu thập ({images.length})</span>
          </UI_SectionTitle>
          {loading ? (
            <UI_LoadingWrapper>
              <Spin size="small" />
              <span style={{ marginLeft: 8 }}>Đang tải ảnh...</span>
            </UI_LoadingWrapper>
          ) : images.length > 0 ? (
            <Image.PreviewGroup
              preview={{
                onChange: (current) => {
                  setCurrentPreviewImage(images[current]);
                },
                onVisibleChange: (visible) => {
                  setPreviewVisible(visible);
                  if (visible && images.length > 0) {
                    setCurrentPreviewImage(images[0]);
                  } else {
                    setCurrentPreviewImage(null);
                  }
                },
              }}
            >
              <UI_ImageGrid>
                {images.map((image, index) => (
                  <UI_ImageItem key={index}>
                    <UI_ImageWrapper>
                      <Image
                        width="100%"
                        height={120}
                        style={{ objectFit: 'cover', borderRadius: '8px' }}
                        src={image.url}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                      />
                    </UI_ImageWrapper>
                    <UI_ImageName title={image.name}>
                      {image.name.length > 20 ? image.name.substring(0, 20) + '...' : image.name}
                    </UI_ImageName>
                    <UI_ImageMeta>
                      {image.user?.fullname && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <UserOutlined style={{ color: '#94a3b8' }} />
                          <span>{image.user.fullname}</span>
                        </div>
                      )}
                      {image.createdAt && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CalendarOutlined style={{ color: '#94a3b8' }} />
                          <DateTimeViewer date={image.createdAt} initialMode="relative" />
                        </div>
                      )}
                    </UI_ImageMeta>
                  </UI_ImageItem>
                ))}
              </UI_ImageGrid>
            </Image.PreviewGroup>
          ) : (
            <UI_NoImages>
              <Empty description="Không có ảnh nào" />
            </UI_NoImages>
          )}
        </UI_ImagesSection>
      </UI_Content>

      {/* Image Preview Metadata Panel - Shows when preview is open */}
      {previewVisible && currentPreviewImage?.metadata && (
        <UI_ImagePreviewMetadataPanel>
          <UI_ImagePreviewMetadataPanelTitle>
            <InfoCircleOutlined style={{ marginRight: 6 }} />
            <span>Thông tin ảnh</span>
          </UI_ImagePreviewMetadataPanelTitle>
          {currentPreviewImage.metadata.address && (
            <UI_ImagePreviewMetadataSection>
              <UI_ImagePreviewMetadataLabel>Địa chỉ</UI_ImagePreviewMetadataLabel>
              <UI_ImagePreviewMetadataValue>{currentPreviewImage.metadata.address}</UI_ImagePreviewMetadataValue>
            </UI_ImagePreviewMetadataSection>
          )}
          {currentPreviewImage.metadata.location?.coords && (
            <>
              <UI_ImagePreviewMetadataSection>
                <UI_ImagePreviewMetadataLabel>Tọa độ</UI_ImagePreviewMetadataLabel>
                <UI_ImagePreviewMetadataValue>
                  {currentPreviewImage.metadata.location.coords.latitude.toFixed(6)}, {currentPreviewImage.metadata.location.coords.longitude.toFixed(6)}
                </UI_ImagePreviewMetadataValue>
              </UI_ImagePreviewMetadataSection>
              <UI_ImagePreviewMetadataSection>
                <UI_ImagePreviewMetadataLabel>Độ cao</UI_ImagePreviewMetadataLabel>
                <UI_ImagePreviewMetadataValue>{currentPreviewImage.metadata.location.coords.altitude?.toFixed(2)} m</UI_ImagePreviewMetadataValue>
              </UI_ImagePreviewMetadataSection>
              <UI_ImagePreviewMetadataSection>
                <UI_ImagePreviewMetadataLabel>Độ chính xác</UI_ImagePreviewMetadataLabel>
                <UI_ImagePreviewMetadataValue>{currentPreviewImage.metadata.location.coords.accuracy?.toFixed(2)} m</UI_ImagePreviewMetadataValue>
              </UI_ImagePreviewMetadataSection>
            </>
          )}
          {currentPreviewImage.metadata.compassHeading !== undefined && (
            <UI_ImagePreviewMetadataSection>
              <UI_ImagePreviewMetadataLabel>Hướng la bàn</UI_ImagePreviewMetadataLabel>
              <UI_ImagePreviewMetadataValue>{currentPreviewImage.metadata.compassHeading.toFixed(2)}°</UI_ImagePreviewMetadataValue>
            </UI_ImagePreviewMetadataSection>
          )}
          {currentPreviewImage.metadata.location?.timestamp && (
            <UI_ImagePreviewMetadataSection>
              <UI_ImagePreviewMetadataLabel>Thời gian</UI_ImagePreviewMetadataLabel>
              <UI_ImagePreviewMetadataValue>
                {formatDateTime(currentPreviewImage.metadata.location.timestamp)}
              </UI_ImagePreviewMetadataValue>
            </UI_ImagePreviewMetadataSection>
          )}
        </UI_ImagePreviewMetadataPanel>
      )}

      {/* Collect Form Schema Inspector Drawer */}
      <CollectFormDetailDrawer
        open={isFormDrawerOpen}
        onClose={() => setIsFormDrawerOpen(false)}
        formUuid={currentFormUuid}
        formData={activeFormData}
        loading={loadingForm}
      />
    </UI_Wrapper>
  );
};

export default memo(GeocollectItemPanel);

const UI_Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e2e8f0;

  &.maximized {
    width: 60vw;
    min-width: 600px;
  }

  @media (max-width: 768px) {
    width: 100vw;
    right: 0;
    left: 0;
    top: var(--header-height);
    height: calc(100vh - var(--header-height));
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    border-left: none;
    border-top: 1px solid #e2e8f0;

    &.maximized {
      width: 100vw;
      min-width: unset;
    }
  }

  @media (max-width: 480px) {
    background: white;
    top: var(--header-height);
    height: calc(100vh - var(--header-height));
  }
`;

const UI_Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  flex-shrink: 0;
`;

const UI_HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const UI_HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 8px;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  flex-shrink: 0;
  margin-top: 2px;
`;

const UI_HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const UI_FormBadge = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  max-width: 100%;
  padding: 2px 8px;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  margin-bottom: 4px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const UI_FormLink = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.15s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;

const UI_HeaderMainTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  line-height: 1.3;
  word-break: break-word;
`;

const UI_HeaderSubtitle = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #64748b;
  line-height: 1.3;
  margin-top: 2px;
`;

const UI_HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const UI_Pager = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #e2e8f0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 2px 4px;
  margin-right: 4px;
`;

const UI_PagerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #cbd5e1;
    color: #0f172a;
  }
`;

const UI_PagerText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #334155;
  font-variant-numeric: tabular-nums;
  padding: 0 3px;
`;

const UI_Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const UI_MetadataSection = styled.div`
  margin-bottom: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
`;

const UI_MetadataHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.05);
  }
`;

const UI_MetadataTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_ExpandButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: transform 0.2s ease;
`;

const UI_MetadataContent = styled.div`
  padding: 12px 16px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
      overflow: hidden;
    }
    to {
      opacity: 1;
      max-height: 500px;
      overflow: visible;
    }
  }
`;

const UI_MetadataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const UI_MetadataLabel = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  min-width: 110px;
`;

const UI_MetadataValue = styled.div`
  font-size: 13px;
  color: #374151;
  text-align: right;
  word-break: break-word;
  font-weight: 400;
`;

const UI_CoordinateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_CoordinateText = styled.span`
  font-family: monospace;
  font-size: 12px;
  background: #ffffff;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #1e293b;
`;

const UI_CopyButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;

  &:hover {
    border-color: #3b82f6 !important;
    background: rgba(59, 130, 246, 0.05) !important;
    color: #3b82f6 !important;
  }
`;

const UI_ImagesSection = styled.div`
  margin-bottom: 24px;
`;

const UI_PropertiesSection = styled.div`
  margin-bottom: 24px;
`;

const UI_SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  font-size: 14px;
  color: #64748b;
`;

const UI_ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`;

const UI_ImageItem = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const UI_ImageWrapper = styled.div`
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
`;

const UI_ImageName = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
  word-break: break-all;
  line-height: 1.3;
`;

const UI_ImageMeta = styled.div`
  font-size: 10px;
  color: #64748b;

  div {
    margin-bottom: 2px;
  }
`;

const UI_NoImages = styled.div`
  text-align: center;
  padding: 24px 16px;
  color: #64748b;
  background: white;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
`;

const UI_ImagePreviewMetadataPanel = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 280px;
  max-height: 400px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 16px;
  z-index: 10000;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const UI_ImagePreviewMetadataPanelTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_ImagePreviewMetadataSection = styled.div`
  margin-bottom: 10px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const UI_ImagePreviewMetadataLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UI_ImagePreviewMetadataValue = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;
  word-break: break-word;
  font-weight: 400;
`;